// One collapsible account section for the redesigned igloos tab — mirrors
// DeviceAccountGroup. Header = account (email + counts); body = one compact line
// per igloo owned by this account (grouped by the igloo's controller family).
// Each line carries: name/id/controller/members, a live badge, a READ-ONLY
// backend badge, a "Settings" button (the per-igloo override editor, mirroring
// DeviceRow's) and a "Metrics" button (enabled only during a live session).
//
// There is deliberately NO bespoke control for any individual setting here: the
// backend is changed through the generic override editor like everything else,
// and the "this causes a Higlo reset" warning lives in that setting's own
// description text (master-igloo-settings.json).
import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ChevronDown, ChevronRight, Users, Snowflake, User, BarChart3, SlidersHorizontal } from 'lucide-react';

// Module-level so the default prop is referentially stable across renders.
const EMPTY_SET = new Set();

export default function IglooAccountGroup({
  account,
  igloos,
  isExpanded,
  onToggle,
  onViewAccountDocument,
  liveSessionsByIgloo,
  overrideIgloos = EMPTY_SET,
  onViewSettings,
  onViewMetrics,
  isOrphan = false,
}) {
  const liveCount = igloos.reduce((n, ig) => n + ((liveSessionsByIgloo[ig.id] || 0) > 0 ? 1 : 0), 0);

  return (
    <div className="border rounded-lg bg-card overflow-hidden">
      {/* Account header — click toggles the group */}
      <div
        className="flex items-center py-2 px-3 cursor-pointer hover:bg-muted/50 text-sm"
        onClick={() => onToggle(account.id)}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isExpanded ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
          <Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="font-medium truncate" title={account.email}>
            {isOrphan ? (
              <span className="text-muted-foreground">{account.email || account.id}</span>
            ) : (
              <>
                <span
                  className="text-blue-600 hover:underline cursor-pointer"
                  onClick={(e) => { e.stopPropagation(); onViewAccountDocument({ type: 'account', id: account.id }); }}
                >
                  {account.email || account.id}
                </span>
                <span className="text-muted-foreground text-xs ml-1">[{account.id}]</span>
              </>
            )}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-shrink-0">
          {liveCount > 0 && <span className="text-green-600">● {liveCount} live</span>}
          <Badge variant="outline" className="text-xs">
            {igloos.length} igloo{igloos.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      </div>

      {/* Body — one line per igloo */}
      {isExpanded && (
        <div className="border-t divide-y">
          {igloos.map((igloo) => {
            const live = liveSessionsByIgloo[igloo.id] || 0;
            // NO per-setting reads here. The row once carried a videoBackend badge
            // resolved from the igloo settings tiers; it was removed (2026-08-07)
            // because the dashboard should not single out one setting. Settings are
            // viewed and changed through the generic settings editors — the row's job
            // is to say a Higlo HAS overrides, not what any one of them is.
            const hasOverrides = overrideIgloos.has(igloo.id);
            // Households in this igloo = union of controller + member family IDs. These
            // igloos keep their families in `controllers` (`members` is usually empty), so
            // counting only the first controller (the old allFamilies) always read "1".
            const memberCount = new Set([
              ...(Array.isArray(igloo.controllers) ? igloo.controllers : []),
              ...(Array.isArray(igloo.members) ? igloo.members : []),
            ]).size;
            return (
              <div key={igloo.id} className="flex items-center gap-3 py-2 px-3 hover:bg-muted/30">
                <Snowflake className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{igloo.iglooName}</p>
                  <p className="text-[11px] text-muted-foreground truncate flex items-center gap-2">
                    <span className="inline-flex items-center gap-1">
                      <User className="w-3 h-3" /> {igloo.controllerName}
                    </span>
                    <span>{igloo.id}</span>
                  </p>
                </div>
                {live > 0 && (
                  <Badge className="bg-green-600 hover:bg-green-600 text-xs flex-shrink-0">● Live · {live}</Badge>
                )}
                <Badge variant="outline" className="text-xs flex-shrink-0">
                  {memberCount} member(s)
                </Badge>
                <Button
                  variant={hasOverrides ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onViewSettings(igloo)}
                  title={hasOverrides ? 'Higlo settings (overrides active)' : 'Higlo settings'}
                  className="flex items-center gap-1 flex-shrink-0"
                >
                  <SlidersHorizontal className="w-4 h-4" /> Settings
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!(live > 0)}
                  onClick={() => onViewMetrics(igloo)}
                  className="flex items-center gap-1 flex-shrink-0"
                >
                  <BarChart3 className="w-4 h-4" /> Metrics
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
